import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import qualityManagementService from '../services/qualityManagement.service';

interface Props {
  open: boolean;
  review: {
    id: number;
    batch_number: string;
    external_issues?: string[] | null;
    description?: string | null;
    action_plan?: string | null;
  } | null;
  onClose: () => void;
  onSaved: () => void;
}

const COMMON_ISSUES = [
  'Color Mismatch',
  'Poor Print Quality',
  'Misalignment',
  'Bleeding',
  'Incomplete Design',
  'Wrong Design',
  'Fabric Damage',
  'Size Issue',
];

const EditExternalRejectionDialog: React.FC<Props> = ({ open, review, onClose, onSaved }) => {
  const [issues, setIssues] = useState<string[]>([]);
  const [newIssue, setNewIssue] = useState('');
  const [description, setDescription] = useState('');
  const [actionPlan, setActionPlan] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && review) {
      setIssues(Array.isArray(review.external_issues) ? [...review.external_issues] : []);
      setDescription(review.description || '');
      setActionPlan(review.action_plan || '');
      setError(null);
    }
  }, [open, review]);

  const handleAddIssue = (issue: string) => {
    if (!issue.trim() || issues.includes(issue.trim())) return;
    setIssues([...issues, issue.trim()]);
    setNewIssue('');
  };

  const handleRemoveIssue = (index: number) => {
    setIssues(issues.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!review) return;
    setSaving(true);
    setError(null);

    try {
      await qualityManagementService.updateExternalRejection(review.id, {
        external_issues: issues.length > 0 ? issues : undefined,
        description: description.trim() || undefined,
        action_plan: actionPlan.trim() || undefined,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update external rejection details');
    } finally {
      setSaving(false);
    }
  };

  if (!review) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: '#D32F2F', color: 'white', pr: 6 }}>
        <Typography variant="h6" fontWeight={700}>
          Edit External Rejection
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          {review.batch_number}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {/* Issues */}
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Issues Identified
        </Typography>
        <Box display="flex" gap={1} mb={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter custom issue or select below"
            value={newIssue}
            onChange={(e) => setNewIssue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddIssue(newIssue);
              }
            }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddIssue(newIssue)}
            disabled={!newIssue.trim()}
          >
            Add
          </Button>
        </Box>
        <Box display="flex" gap={0.5} flexWrap="wrap" mb={2}>
          {COMMON_ISSUES.map((issue) => (
            <Chip
              key={issue}
              label={issue}
              size="small"
              onClick={() => handleAddIssue(issue)}
              color={issues.includes(issue) ? 'error' : 'default'}
              variant={issues.includes(issue) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
        {issues.length > 0 && (
          <Box display="flex" gap={0.5} flexWrap="wrap" mb={2}>
            {issues.map((issue, i) => (
              <Chip
                key={i}
                label={issue}
                onDelete={() => handleRemoveIssue(i)}
                color="error"
                deleteIcon={<DeleteIcon />}
              />
            ))}
          </Box>
        )}

        {/* Description */}
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the rejection details..."
          sx={{ mb: 2 }}
        />

        {/* Action Plan */}
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Action Plan"
          value={actionPlan}
          onChange={(e) => setActionPlan(e.target.value)}
          placeholder="What actions will be taken..."
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ bgcolor: '#D32F2F', '&:hover': { bgcolor: '#B71C1C' } }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditExternalRejectionDialog;
